import { loans, milestones } from '../data/portal'
import type { Loan } from '../types/portal'

interface Reply {
  text: string
  source: string
}

const BLOCKED = /\b(income|credit|score|rates?|apr|amount|fees?|assets?|dti|debt|salary|bank|down payment|payments?|taxes)\b/

const inProcess = loans.filter((l) => l.status === 'in-process')

function findLoan(q: string): Loan | undefined {
  return loans.find((l) => {
    const first = l.buyer.split(' ')[0].toLowerCase()
    const words = [l.lastName.toLowerCase(), first, l.streetKey]
    return words.some((w) => new RegExp(`\\b${w}\\b`).test(q))
  })
}

const milestoneSource = (l: Loan) => `Loan milestones · updated ${l.lastUpdate}`
const teamSource = (l: Loan) => `File team · updated ${l.lastUpdate}`

export function getReply(input: string): Reply {
  const q = input.toLowerCase()

  if (BLOCKED.test(q)) {
    return {
      text: 'I can’t share financial, credit or income details for any buyer — those stay between the buyer and their loan officer. I’m happy to help with milestones, dates and who’s on the team.',
      source: 'Partner sharing policy',
    }
  }

  const loan = findLoan(q)

  if (!loan) {
    if (/left|clear to close|remaining|ctc/.test(q)) {
      return {
        text: 'Here’s where each file stands: Priya Chen (88 Riverside Dr) is already Clear to Close for Oct 30. Jordan Rivera is in underwriting — conditional approval comes next. Marcus Bell is waiting on the appraisal, and Dana Kowalski just completed her application. Ask about a specific buyer for more detail.',
        source: 'Loan milestones · updated Oct 21',
      }
    }
    if (/clos|this month|scheduled/.test(q)) {
      const list = inProcess.map((l) => `${l.buyer} — ${l.estCloseShort}`).join('; ')
      return { text: `Upcoming estimated closings: ${list}.`, source: 'Loan milestones · updated Oct 21' }
    }
    return {
      text: `I can answer questions about ${inProcess.map((l) => l.buyer).join(', ')} and your closed files. Try asking about a closing date, the appraisal, or who’s on the team.`,
      source: 'Your files · updated Oct 21',
    }
  }

  const [street] = [loan.street]
  const idx = milestones.indexOf(loan.milestone)

  if (/processor|closer|officer|\bwho\b|team|contact/.test(q)) {
    const role = /processor/.test(q) ? 'Processor' : /closer/.test(q) ? 'Closer' : /officer/.test(q) ? 'Loan officer' : null
    if (role) {
      const m = loan.team.find((t) => t.role === role)
      if (m) {
        return {
          text: `The ${role.toLowerCase()} on the ${loan.lastName} file (${street}) is ${m.name}. You can reach them at ${m.phone} or ${m.email}.`,
          source: teamSource(loan),
        }
      }
    }
    return {
      text: `The team on ${loan.buyer}’s file: ${loan.team.map((t) => `${t.name} (${t.role.toLowerCase()})`).join(', ')}.`,
      source: teamSource(loan),
    }
  }

  if (/apprais/.test(q)) {
    if (loan.appraisalReceived) {
      return {
        text: `Yes. The appraisal for ${loan.buyer} at ${street}, ${loan.city.replace(',', '')} was received ${loan.appraisalReceived.replace(', 2026', '')}. The file is at ${loan.milestone} and estimated closing is ${loan.estCloseShort}.`,
        source: milestoneSource(loan),
      }
    }
    if (loan.appraisalOrdered) {
      return {
        text: `Not yet. The appraisal for ${street} was ordered ${loan.appraisalOrdered.replace(', 2026', '')} and the inspection is scheduled. Estimated closing is still ${loan.estCloseShort}.`,
        source: milestoneSource(loan),
      }
    }
    return {
      text: `The appraisal hasn’t been ordered yet for ${street}. It’s typically ordered shortly after the application is complete.`,
      source: milestoneSource(loan),
    }
  }

  if (/left|clear to close|remaining|next/.test(q)) {
    if (loan.milestone === 'Closed') {
      return { text: `${loan.buyer}’s loan closed on ${loan.estClose}. Nothing left to do.`, source: milestoneSource(loan) }
    }
    if (loan.milestone === 'Clear to Close') {
      return {
        text: `Nothing — ${loan.buyer}’s file is Clear to Close. Closing is scheduled for ${loan.estClose} with ${loan.team.find((t) => t.role === 'Closer')?.name}.`,
        source: milestoneSource(loan),
      }
    }
    const remaining = milestones.slice(idx + 1, 6)
    return {
      text: `${loan.buyer}’s file is at ${loan.milestone}. Still ahead before closing: ${remaining.join(', ')}. Specific underwriting conditions aren’t shared here, but the team is on track for ${loan.estCloseShort}.`,
      source: milestoneSource(loan),
    }
  }

  if (/clos|when|date/.test(q)) {
    if (loan.milestone === 'Closed') {
      return { text: `${loan.buyer}’s loan on ${street} closed ${loan.estClose}.`, source: milestoneSource(loan) }
    }
    return {
      text: `Estimated closing for ${loan.buyer} at ${street} is ${loan.estClose}. The file is currently at ${loan.milestone}, and the date matches the contract.`,
      source: milestoneSource(loan),
    }
  }

  return {
    text: `${loan.buyer} (${street}, ${loan.city}) is at ${loan.milestone}${loan.milestone === 'Closed' ? '' : `, with estimated closing ${loan.estCloseShort}`}. Latest update ${loan.updates[0].date}: ${loan.updates[0].text}`,
    source: milestoneSource(loan),
  }
}
